import { Group, Text, useComputedColorScheme, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { LogoMark } from './LogoMark';
import { useCompanyModules } from '../../hooks/useCompanyModules';

interface LogoProps {
  showSubtitle?: boolean;
  /** Visual variant. `onDark` adapts colors for a dark/gradient background
   * (used on the auth page brand panel). */
  variant?: 'default' | 'onDark';
  /** Suppresses the click → /dashboard navigation (e.g. for the auth page,
   * where the user isn't signed in yet). */
  disableNav?: boolean;
  /** Optional subtitle override. When provided, replaces the default
   * `t('logo.subtitle')` string. */
  subtitle?: string;
  /** Render just the square logo mark, without the company name/subtitle.
   * Used by the collapsed (icon-only) sidebar rail. */
  iconOnly?: boolean;
  /** Turns the logo into a plain link with a full page load. For pages outside the app
   * (the auth screen): `/` is the static landing served by nginx, not an SPA route. */
  href?: string;
  /** Название вместо компании домена: на `/demo` домен — чужая компания. */
  name?: string;
}

export function Logo({
  showSubtitle = true,
  variant = 'default',
  disableNav = false,
  subtitle,
  iconOnly = false,
  href,
  name,
}: LogoProps) {
  const colorScheme = useComputedColorScheme('light');
  const isDark = colorScheme === 'dark';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branding } = useTenant();
  // Подпись — про карту компетенций: у компании без этого раздела её нет.
  const { hasModule } = useCompanyModules();
  const hasSubtitle = showSubtitle && (subtitle !== undefined || hasModule('competencyMap'));

  const handleLogoClick = () => {
    if (disableNav) return;
    navigate('/dashboard');
  };

  const onDark = variant === 'onDark';
  // Фон и рамка — только под чужой логотип: у собственного знака своя плитка.
  const boxBg = !branding.logoUrl
    ? undefined
    : onDark
      ? 'rgba(255,255,255,0.18)'
      : isDark
        ? 'var(--mantine-color-dark-6)'
        : '#f1f3f4';
  const boxBorder = !branding.logoUrl
    ? undefined
    : onDark
      ? '1px solid rgba(255,255,255,0.25)'
      : isDark
        ? '1px solid var(--mantine-color-dark-4)'
        : '1px solid #e1e5e9';

  return (
    <UnstyledButton
      {...(href ? { component: 'a' as const, href } : { onClick: handleLogoClick })}
      style={{
        cursor: disableNav && !href ? 'default' : 'pointer',
        transition: 'opacity 0.2s ease',
      }}
      onMouseEnter={(e) => {
        if (!disableNav || href) e.currentTarget.style.opacity = '0.8';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '1';
      }}
    >
      <Group p={iconOnly ? 0 : showSubtitle ? 'md' : 0} gap="sm" justify="flex-start" wrap="nowrap">
        <div
          style={{
            flexShrink: 0,
            width: 32,
            height: 32,
            backgroundColor: boxBg,
            borderRadius: 8,
            border: boxBorder,
            backdropFilter: onDark ? 'blur(4px)' : undefined,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: iconOnly ? 0 : hasSubtitle ? 18 : 0,
          }}
        >
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt=""
              style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover' }}
            />
          ) : (
            // На тёмной панели входа знак стоит на своей плитке, в меню — без
            // неё: рядом со светлыми значками навигации тёмный квадрат тяжелее
            // всего остального.
            <LogoMark size={onDark ? 32 : 28} withTile={onDark} />
          )}
        </div>
        {!iconOnly && (
          <div>
            <Text
              fw={700}
              size="lg"
              c={onDark ? 'white' : undefined}
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            >
              {name ?? branding.companyName}
            </Text>
            {hasSubtitle && (
              <Text
                size="xs"
                c={onDark ? undefined : 'dimmed'}
                style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  color: onDark ? 'rgba(255,255,255,0.75)' : undefined,
                }}
              >
                {subtitle ?? t('logo.subtitle')}
              </Text>
            )}
          </div>
        )}
      </Group>
    </UnstyledButton>
  );
}
